import type { DebouncedFunc } from 'lodash';

import { GeneratedImage } from 'components/BackgroundReplacer/Gallery/types';
import { BackgroundGenerationOptions } from 'components/BackgroundReplacer/types';
import { ReplaceBackgroundResponse } from 'global/generatorsApi';

export enum GenerateImageError {
  UNSAFE_PROMPT,
  NO_SOURCE_IMAGE,
  UPLOAD_SPACE_LIMIT_REACHED,
  UNKNOWN,
}

export enum RemoveBackgroundError {
  UPLOAD_SPACE_LIMIT_REACHED = 'Your image could not be uploaded. Upload space limit reached.',
  UNKNOWN_UPLOAD_ERROR = 'Your image could not be uploaded. Try again.',
  UNKNOWN_BACKGROUND_REMOVER_ERROR = 'Background could not be removed. Try again.',
}

export type ItemsAndPagesState = {
  items: GeneratedImage[];
  pages: { current: number; total: number } | null;
};
export type InitialImageState = ItemsAndPagesState & {
  initialImage: string;
  dimensions: { width: number; height: number };
};
export type ProductImageState = InitialImageState & { productImage: string };
export type EditingImageState = ProductImageState & { editingIsReady: boolean };
export type GeneratingState = EditingImageState & { inProgress: boolean };
export const createStateWithInitialImage = (
  object: ItemsAndPagesState,
  initialImage: string,
  dimensions: { width: number; height: number }
): InitialImageState => {
  return {
    items: object.items,
    pages: object.pages,
    initialImage: initialImage,
    dimensions: dimensions,
  };
};
export const createStateWithProductImage = (
  object: InitialImageState,
  productImage: string
): ProductImageState => {
  return {
    items: object.items,
    pages: object.pages,
    initialImage: object.initialImage,
    dimensions: object.dimensions,
    productImage: productImage,
  };
};
export const createStateWithEditingImage = (
  object: ProductImageState,
  editingIsReady: boolean
): EditingImageState => {
  return {
    items: object.items,
    pages: object.pages,
    initialImage: object.initialImage,
    dimensions: object.dimensions,
    productImage: object.productImage,
    editingIsReady: editingIsReady,
  };
};
export const createStateWithProgress = (
  object: EditingImageState,
  inProgress: boolean
): GeneratingState => {
  return {
    items: object.items,
    pages: object.pages,
    initialImage: object.initialImage,
    dimensions: object.dimensions,
    productImage: object.productImage,
    editingIsReady: object.editingIsReady,
    inProgress: inProgress,
  };
};
export const hasItemsAndPages = (
  state: WorkflowState
): state is ItemsAndPagesState => {
  return state !== 'loading' && 'items' in state && 'pages' in state;
};
export const hasInitialImage = (
  state: WorkflowState
): state is InitialImageState => {
  return state !== 'loading' && 'initialImage' in state;
};
export const hasProductImage = (
  state: WorkflowState
): state is ProductImageState => {
  return state !== 'loading' && 'productImage' in state;
};
export const imageCanBeEdited = (
  state: WorkflowState
): state is EditingImageState => {
  return state !== 'loading' && 'editingIsReady' in state;
};
export const canBeInProgress = (
  state: WorkflowState
): state is GeneratingState => {
  return state !== 'loading' && 'inProgress' in state;
};
export type WorkflowState =
  | 'loading'
  | ItemsAndPagesState
  | InitialImageState
  | ProductImageState
  | EditingImageState
  | GeneratingState;

export interface State {
  state: WorkflowState;
  initializedFor: string | null;
  itemsPerPage: number;
  initialize: (
    itemsPerPage: number,
    userId: string,
    presetLabels: Record<string, string>
  ) => Promise<void>;
  getEditedImage: (() => Promise<File | null>) | null;
  presetLabels: Record<string, string>;
  reset: () => void;
  updateItemsPerPage: (value: number) => void;
  updatePresetLabels: (value: Record<string, string>) => void;
  // We should not spam api if the image no longer visible. Save and clear long polling timeout if the page changed.
  imagesTimeout: Record<string, ReturnType<typeof setTimeout>>;
  _selectGeneratedContent: (
    page: number,
    itemsPerPage: number
  ) => Promise<{
    items: GeneratedImage[];
    pages: { current: number; total: number } | null;
  }>;
  _clearUpdateIntervals: () => void;
  _updateGeneratedContent: DebouncedFunc<
    (value: number, itemsPerPage: number) => Promise<void>
  >;
  // null -> update current page
  selectPage: (value: number | null, itemsPerPage: number) => Promise<void>;
  _scheduleLoadingImagesCheck: () => void;
  _updateImageState: (id: string) => void;
  _handleResponse: (response: ReplaceBackgroundResponse) => void;
  _updateImage: (
    response: ReplaceBackgroundResponse & { success: true }
  ) => void;
  selectImage: (
    file: File,
    replace: boolean
  ) => Promise<{ error?: RemoveBackgroundError }>;
  generatePredefinedImages: (
    limit: number,
    onGenerated: () => void
  ) => Promise<void>;
  generateImage: (
    options: BackgroundGenerationOptions
  ) => Promise<{ error?: GenerateImageError }>;
  tryDeleteImage: (id: string) => Promise<boolean>;
  registerGetEditedImageHandler: (handler: () => Promise<File | null>) => void;
  setCanBeEdited: (value: boolean) => void;
}
